"use client";

import React from "react";
import { AlertTriangle } from "lucide-react";
import { KYCProblem, getProblemDescription } from "@/types/models/kyc";

interface KYCFieldErrorProps {
  fieldName: string;
  problems: KYCProblem[];
  className?: string;
}

/**
 * Component to display field-specific KYC errors
 * Use this to highlight individual form fields that have validation issues
 */
export const KYCFieldError: React.FC<KYCFieldErrorProps> = ({
  fieldName,
  problems,
  className = "",
}) => {
  // Map field names to their corresponding problem types
  const fieldProblemMap: Record<string, KYCProblem[]> = {
    business_name: ["business_name_mismatch"],
    business_logo: ["business_logo_mismatch"],
    business_email: ["business_email_mismatch"],
    business_phone_number: ["business_phone_number_mismatch"],
    address: ["address_mismatch"],
    address_line_one: ["address_mismatch"],
    address_line_two: ["address_mismatch"],
    city: ["address_mismatch"],
    state: ["address_mismatch"],
    postal_code: ["address_mismatch"],
    gst_number: ["gst_number_mismatch"],
    aadhaar_document_front: ["aadhaar_document_mismatch"],
    aadhaar_document_back: ["aadhaar_document_mismatch"],
    pan_document: ["pan_document_mismatch"],
    address_proof_document: ["address_proof_document_mismatch"],
    video_kyc_file: ["video_kyc_mismatch"],
  };

  // Check if this field has any problems
  const fieldProblems = fieldProblemMap[fieldName] || [];
  const hasProblems = fieldProblems.some((fieldProblem) =>
    problems.includes(fieldProblem)
  );

  if (!hasProblems) {
    return null;
  }

  // Get the specific problems for this field
  const specificProblems = fieldProblems.filter((fieldProblem) =>
    problems.includes(fieldProblem)
  );

  return (
    <div className={`mt-2 ${className}`}>
      {specificProblems.map((problem, index) => (
        <div
          key={index}
          className="flex items-start space-x-2 p-2 bg-orange-50 border border-orange-200 rounded-lg"
        >
          <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-orange-700">
            <p className="font-medium">Field requires attention</p>
            <p className="text-xs mt-1">{getProblemDescription(problem)}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Hook to check if a field has errors
 */
export const useFieldHasErrors = (fieldName: string, problems: KYCProblem[] = []) => {
  const fieldProblemMap: Record<string, KYCProblem[]> = {
    business_name: ["business_name_mismatch"],
    business_logo: ["business_logo_mismatch"],
    business_email: ["business_email_mismatch"],
    business_phone_number: ["business_phone_number_mismatch"],
    address: ["address_mismatch"],
    address_line_one: ["address_mismatch"],
    address_line_two: ["address_mismatch"],
    city: ["address_mismatch"],
    state: ["address_mismatch"],
    postal_code: ["address_mismatch"],
    gst_number: ["gst_number_mismatch"],
    aadhaar_document_front: ["aadhaar_document_mismatch"],
    aadhaar_document_back: ["aadhaar_document_mismatch"],
    pan_document: ["pan_document_mismatch"],
    address_proof_document: ["address_proof_document_mismatch"],
    video_kyc_file: ["video_kyc_mismatch"],
  };

  const fieldProblems = fieldProblemMap[fieldName] || [];
  return fieldProblems.some((fieldProblem) => problems.includes(fieldProblem));
};

export default KYCFieldError;